<script setup lang="ts">
/** One entry in a task's story — a comment, or a system event, rendered inline
 *  with the comments rather than in a separate log. */
import { computed } from "vue";
import { useBoard } from "../stores/board.js";
import type { TaskEvent } from "@shared/types.js";
import { formatHours } from "../lib/format.js";
import MarkdownText from "./MarkdownText.vue";

const props = defineProps<{ entry: TaskEvent }>();
const board = useBoard();

const who = computed(() => board.userName(props.entry.userId));

const when = computed(() =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(props.entry.createdAt),
);

const summary = computed(() => {
  const d = (props.entry.data ?? {}) as Record<string, unknown>;
  switch (props.entry.kind) {
    case "created":
      return "created this task";
    case "status":
      // An automatic change had no hand on it; saying "moved it" would be a lie
      // about who did what.
      if (d.auto) {
        if (d.to === "done") return "— completed automatically: everything under it is done";
        return d.from === "todo"
          ? "— started automatically: something under it was started"
          : "— reopened automatically: something under it is not done";
      }
      return `moved it ${String(d.from)} → ${String(d.to)}`;
    case "branch":
      return d.to ? `set the branch to ${String(d.to)}` : "cleared the branch";
    case "title":
      return `renamed it from “${String(d.from)}”`;
    case "timer":
      // Hours typed in by hand; a negative one took some back. The rest are
      // from the start/stop timer, which is gone but still in old timelines.
      if (d.action === "log") {
        const h = Number(d.hours ?? 0);
        return h < 0 ? `took back ${formatHours(-h)}` : `logged ${formatHours(h)}`;
      }
      if (d.action === "start") return "started the timer";
      // Stopped by the task being finished or sent to review, possibly by
      // someone else. Events from before review stopped it carry no `to`.
      return d.auto
        ? `logged ${formatHours(Number(d.hours ?? 0))} — timer stopped automatically when the task ${
            d.to === "review" ? "went to review" : "was done"
          }`
        : `logged ${formatHours(Number(d.hours ?? 0))}`;
    case "claude":
      switch (d.action) {
        case "started":
          return `started working on ${String(d.branch)}`;
        case "succeeded": {
          const commits = Number(d.commits ?? 0);
          return `finished ${String(d.branch)}: ${commits} commit${commits === 1 ? "" : "s"}${d.pushed ? ", pushed" : ""}`;
        }
        case "failed":
          return `Claude's run on ${String(d.branch)} failed: ${String(d.error ?? "no reason given")}`;
        case "cancelled":
          return d.wasRunning ? `stopped Claude's run on ${String(d.branch)}` : `cancelled the queued run on ${String(d.branch)}`;
        default:
          // "queued", and events written before runs had actions.
          return `asked Claude to work on it: ${String(d.branch)} from ${String(d.baseBranch)}, in worktree ${String(d.worktree)}`;
      }
    case "schedule":
      return d.startMs || d.endMs ? "rescheduled it" : "cleared the schedule";
    case "assign": {
      const added = (d.added as string[] | undefined) ?? [];
      const removed = (d.removed as string[] | undefined) ?? [];
      const bits: string[] = [];
      if (added.length) bits.push(`assigned ${added.map(board.userName).join(", ")}`);
      if (removed.length) bits.push(`unassigned ${removed.map(board.userName).join(", ")}`);
      return bits.join(" and ");
    }
    default:
      return props.entry.kind;
  }
});
</script>

<template>
  <div class="entry">
    <span class="avatar" :style="{ background: board.userColor(entry.userId) }">
      {{ who.slice(0, 2) }}
    </span>
    <div class="entry-body">
      <div class="entry-meta">{{ who }} · {{ when }}</div>
      <MarkdownText v-if="entry.kind === 'comment'" class="entry-text" :text="entry.body ?? ''" />
      <div v-else class="entry-sys">{{ summary }}</div>
    </div>
  </div>
</template>
